/**
 * Batch #14.16 — Admin : Liste des soumissions T777 (Bureau à domicile) clients.
 *
 * Affiche toutes les déclarations T777 simples soumises par les clients via le
 * portail. Filtrable par année fiscale + statut (envoyées / brouillons).
 *
 * Route admin : /admin/t777-submissions
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Home, FileText, Calendar, Mail, Filter,
  CheckCircle2, Clock, Loader2, Trash2, Download,
} from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH } from "@/api";


function fmt(n) {
  return Number(n || 0).toLocaleString("fr-CA", { style: "currency", currency: "CAD" });
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-CA", { dateStyle: "medium" });
  } catch { return iso; }
}

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2];

export default function T777Submissions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState("");
  const [submittedOnly, setSubmittedOnly] = useState(true);
  const [selected, setSelected] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (year) params.append("tax_year", year);
      if (submittedOnly) params.append("submitted_only", "true");
      const r = await axios.get(`${API}/admin/t777-summaries?${params}`, { headers: authH() });
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [year, submittedOnly]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (s, e) => {
    e.stopPropagation();
    if (!window.confirm(
      `Supprimer définitivement la déclaration T777 de ${s.client_name} (${s.tax_year}) ?\n\n` +
      `Cette action est IRRÉVERSIBLE.`
    )) return;
    setDeletingId(s.id);
    try {
      await axios.delete(`${API}/admin/t777-summaries/${s.id}`, { headers: authH() });
      toast.success("Déclaration supprimée");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de suppression");
    } finally {
      setDeletingId(null);
    }
  };

  const toggleReviewed = async (s, e) => {
    e.stopPropagation();
    const reviewed = !s.admin_reviewed_at;
    try {
      await axios.post(`${API}/admin/t777-summaries/${s.id}/review?reviewed=${reviewed}`, {}, { headers: authH() });
      toast.success(reviewed ? "Marquée traitée — retirée de la pastille" : "Remise à traiter");
      window.dispatchEvent(new CustomEvent("badges:refresh"));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleDownloadPdf = async (s) => {
    // PDF « Bureau à domicile » filtré par client + année
    setDownloadingId(s.id);
    try {
      const params = new URLSearchParams();
      if (s.tax_year) params.append("tax_year", s.tax_year);
      if (s.client_id) params.append("client_id", s.client_id);
      const r = await axios.get(`${API}/t777/export/pdf?${params}`, {
        headers: authH(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const safe = (s.client_name || "client").replace(/\s+/g, "_");
      a.download = `bureau_domicile_${safe}_${s.tax_year || "all"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF téléchargé");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur PDF");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="space-y-5 p-4 sm:p-6 max-w-6xl mx-auto" data-testid="admin-t777-submissions">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Home className="w-6 h-6 text-amber-600" />
            Déclarations Bureau à domicile (T777)
          </h2>
          <p className="text-sm text-slate-500">
            Soumissions des clients via le portail — formulaire annuel simplifié
          </p>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-center gap-4">
        <Filter className="w-4 h-4 text-slate-400" />
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-slate-600">Année :</label>
          <select
            value={year}
            onChange={(e) => setYear(e.target.value)}
            className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm"
            data-testid="filter-year"
          >
            <option value="">Toutes</option>
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={submittedOnly}
            onChange={(e) => setSubmittedOnly(e.target.checked)}
            data-testid="filter-submitted"
          />
          Envoyées uniquement
        </label>
        <span className="text-xs text-slate-500 ml-auto">
          {loading ? "..." : `${items.length} déclaration(s)`}
        </span>
      </div>

      {/* Liste */}
      {loading && (
        <div className="text-center py-12 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-12 text-center" data-testid="empty-state">
          <Home className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-500">Aucune déclaration trouvée pour ces filtres.</p>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="space-y-2" data-testid="submissions-list">
          {items.map((s) => (
            <button
              key={s.id}
              onClick={() => setSelected(s)}
              className="w-full bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md hover:border-amber-300 transition text-left"
              data-testid={`submission-${s.id}`}
            >
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5 text-amber-700" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{s.client_name}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-3 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> Année {s.tax_year}
                      </span>
                      {s.client_email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {s.client_email}
                        </span>
                      )}
                      {s.submitted ? (
                        <span className="flex items-center gap-1 text-emerald-700">
                          <CheckCircle2 className="w-3 h-3" /> Envoyée le {fmtDate(s.submitted_at)}
                          {s.admin_reviewed_at
                            ? <span className="ml-1 px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-[10px]" data-testid={`t777-reviewed-${s.id}`}>Traitée</span>
                            : <span className="ml-1 px-1.5 py-0.5 rounded bg-rose-50 border border-rose-200 text-rose-700 text-[10px]" data-testid={`t777-pending-${s.id}`}>À traiter</span>}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-amber-700">
                          <Clock className="w-3 h-3" /> Brouillon
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-right shrink-0 flex items-center gap-2">
                  <div>
                    <div className="text-lg font-bold text-amber-700">
                      {fmt(s.total_expenses)}
                    </div>
                    <div className="text-xs text-slate-500">{(s.space_pct || 0).toFixed(1)} % bureau</div>
                  </div>
                  {s.submitted && (
                    <button
                      type="button"
                      onClick={(e) => toggleReviewed(s, e)}
                      className={`text-xs px-2 py-1 rounded border inline-flex items-center gap-1 ${s.admin_reviewed_at ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}
                      data-testid={`t777-review-${s.id}`}
                      title={s.admin_reviewed_at ? "Remettre à traiter" : "Marquer traité (retire de la pastille Files PMS)"}
                    >
                      {s.admin_reviewed_at ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
                      {s.admin_reviewed_at ? "Traitée" : "Marquer traité"}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleDownloadPdf(s); }}
                    disabled={downloadingId === s.id}
                    className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-1.5 rounded disabled:opacity-50"
                    data-testid={`t777-pdf-${s.id}`}
                    title="Télécharger le PDF Bureau à domicile"
                  >
                    {downloadingId === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => handleDelete(s, e)}
                    disabled={deletingId === s.id}
                    className="text-rose-500 hover:text-rose-700 hover:bg-rose-50 p-1.5 rounded disabled:opacity-50"
                    data-testid={`t777-delete-${s.id}`}
                    title="Supprimer définitivement"
                  >
                    {deletingId === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Détail modal */}
      {selected && (
        <SubmissionDetailModal submission={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

function SubmissionDetailModal({ submission, onClose }) {
  const s = submission;
  const rows = [
    ["Électricité", s.electricity],
    ["Chauffage", s.heating],
    ["Internet", s.internet],
    ["Loyer", s.rent],
    ["Intérêts hypothécaires", s.mortgage_interest],
    ["Taxes foncières", s.property_tax],
    ["Assurance habitation", s.home_insurance],
    ["Entretien et réparations", s.maintenance],
    [`Autres ${s.other_expenses_label ? `(${s.other_expenses_label})` : ""}`, s.other_expenses],
  ];
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose} data-testid="detail-modal">
      <div
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-r from-amber-50 to-amber-100 px-6 py-4 border-b border-amber-200 flex items-center justify-between sticky top-0">
          <div>
            <h3 className="text-lg font-bold text-amber-900">Déclaration T777 — {s.client_name}</h3>
            <p className="text-sm text-amber-800">Année fiscale {s.tax_year}</p>
          </div>
          <button onClick={onClose} className="text-amber-700 hover:text-amber-900 font-bold text-xl">×</button>
        </div>

        <div className="p-6 space-y-5">
          {/* Mesures logement */}
          <section>
            <h4 className="text-xs font-bold uppercase text-slate-500 mb-2">Mesures du logement</h4>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="text-xs text-slate-500">Superficie totale</div>
                <div className="font-bold text-slate-900">{s.home_total_sqft || 0} pi²</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="text-xs text-slate-500">Bureau</div>
                <div className="font-bold text-slate-900">{s.office_sqft || 0} pi²</div>
              </div>
              <div className="bg-blue-50 rounded-lg p-3">
                <div className="text-xs text-blue-700">% bureau</div>
                <div className="font-bold text-blue-900">{(s.space_pct || 0).toFixed(2)} %</div>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              {s.hours_per_week || 0} h travaillées / semaine
            </p>
          </section>

          {/* Dépenses */}
          <section>
            <h4 className="text-xs font-bold uppercase text-slate-500 mb-2">Dépenses annuelles déclarées</h4>
            <div className="space-y-1 text-sm">
              {rows.map(([label, val]) => (
                <div key={label} className="flex justify-between py-1.5 border-b border-slate-100">
                  <span className="text-slate-700">{label}</span>
                  <span className="font-semibold text-slate-900">{fmt(val)}</span>
                </div>
              ))}
              <div className="flex justify-between pt-3 mt-2 border-t-2 border-amber-300">
                <span className="font-bold text-amber-900">TOTAL</span>
                <span className="font-bold text-amber-900 text-lg">{fmt(s.total_expenses)}</span>
              </div>
            </div>
          </section>

          {/* Notes */}
          {s.notes && (
            <section>
              <h4 className="text-xs font-bold uppercase text-slate-500 mb-2">Notes du client</h4>
              <p className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-3 whitespace-pre-wrap">
                {s.notes}
              </p>
            </section>
          )}

          {/* Métadonnées */}
          <section className="pt-3 border-t border-slate-200 text-xs text-slate-500 flex flex-wrap gap-4">
            <span>Créé : {fmtDate(s.created_at)}</span>
            <span>Modifié : {fmtDate(s.updated_at)}</span>
            {s.submitted_at && <span>Envoyé : {fmtDate(s.submitted_at)}</span>}
          </section>
        </div>
      </div>
    </div>
  );
}
